import { z } from 'zod';
export const LanguageEnum = z.enum([
    'javascript',
    'typescript',
    'python',
    'java',
    'csharp',
    'go',
    'rust',
    'cpp',
    'php',
    'ruby',
]);
export const CallTypeEnum = z.enum(['direct', 'indirect', 'dynamic', 'recursive']);
export const RuleTypeEnum = z.enum([
    'VALIDATION',
    'CALCULATION',
    'AUTHORIZATION',
    'WORKFLOW',
    'BUSINESS_CONSTRAINT',
]);
export const RationaleConfidenceEnum = z.enum(['high', 'medium', 'low', 'requires_validation']);
export const ExtractionMethodEnum = z.enum(['AST', 'LLM', 'HYBRID']);
export const ModuleTypeEnum = z.enum(['module', 'sub_module', 'shared_utilities']);
export const JobStatusEnum = z.enum([
    'PENDING',
    'RUNNING',
    'COMPLETED',
    'FAILED',
    'PAUSED',
    'CANCELLED',
]);
export const NodeTypeEnum = z.enum([
    'FILE',
    'FUNCTION',
    'CLASS',
    'MODULE',
    'BUSINESS_RULE',
    'EXTERNAL_LIBRARY',
]);
export const EdgeTypeEnum = z.enum([
    'CALLS',
    'IMPORTS',
    'CONTAINS',
    'IMPLEMENTS',
    'EXTENDS',
    'BELONGS_TO',
]);
export const AnalyzeFileRequestSchema = z.object({
    repositoryId: z.string().cuid(),
    filePath: z.string().min(1),
    reAnalyze: z.boolean().optional(),
});
export const AnalyzeRepositoryRequestSchema = z.object({
    repositoryId: z.string().cuid(),
    fileFilters: z.array(z.string()).optional(),
    reAnalyze: z.boolean().optional(),
});
export const QueryBusinessRulesSchema = z.object({
    repositoryId: z.string().cuid(),
    ruleType: RuleTypeEnum.optional(),
    businessDomain: z.string().optional(),
    minConfidence: z.number().min(0).max(1).optional(),
    page: z.number().int().positive().optional().default(1),
    limit: z.number().int().positive().max(100).optional().default(20),
});
export const QueryCallGraphSchema = z.object({
    repositoryId: z.string().cuid(),
    functionName: z.string().optional(),
    depth: z.number().int().positive().max(10).optional().default(3),
});
export const QueryDeadCodeSchema = z.object({
    repositoryId: z.string().cuid(),
});
export const QueryKnowledgeGraphSchema = z.object({
    repositoryId: z.string().cuid(),
    nodeTypes: z.array(NodeTypeEnum).optional(),
    edgeTypes: z.array(EdgeTypeEnum).optional(),
    depth: z.number().int().positive().max(10).optional().default(3),
    centerNodeId: z.string().cuid().optional(),
});
export const AnalyzeFileResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    fileId: z.string().cuid().optional(),
    astId: z.string().cuid().optional(),
    entitiesExtracted: z.number().int().optional(),
    businessRulesExtracted: z.number().int().optional(),
});
export const AnalysisJobResponseSchema = z.object({
    jobId: z.string().cuid(),
    repositoryId: z.string().cuid(),
    status: JobStatusEnum,
    totalFiles: z.number().int(),
    processedFiles: z.number().int(),
    failedFiles: z.number().int(),
    progress: z.number().min(0).max(100),
    startedAt: z.string().datetime().optional(),
    completedAt: z.string().datetime().optional(),
    errorDetails: z.string().optional(),
});
export const AnalysisJobStatusSchema = z.object({
    job: AnalysisJobResponseSchema,
});
export const CallGraphNodeSchema = z.object({
    id: z.string().cuid(),
    name: z.string(),
    type: z.enum(['function', 'external']),
    fileId: z.string().cuid(),
    filePath: z.string(),
    lineStart: z.number().int(),
    lineEnd: z.number().int(),
    signature: z.string().optional(),
    complexity: z.string().optional(),
});
export const CallGraphEdgeSchema = z.object({
    from: z.string().cuid(),
    to: z.string().cuid(),
    callType: CallTypeEnum,
    lineNumber: z.number().int(),
    isExternal: z.boolean(),
});
export const CallGraphResponseSchema = z.object({
    nodes: z.array(CallGraphNodeSchema),
    edges: z.array(CallGraphEdgeSchema),
    metrics: z.object({
        totalFunctions: z.number().int(),
        totalCalls: z.number().int(),
        externalCalls: z.number().int(),
        recursiveFunctions: z.number().int(),
    }),
});
export const BusinessRuleSchema = z.object({
    id: z.string().cuid(),
    ruleId: z.string(),
    ruleType: RuleTypeEnum,
    ruleCategory: z.string().optional(),
    ruleDescription: z.string(),
    condition: z.string().optional(),
    action: z.string().optional(),
    businessRationale: z.string().optional(),
    rationaleConfidence: RationaleConfidenceEnum.optional(),
    fileId: z.string().cuid(),
    filePath: z.string(),
    functionName: z.string().optional(),
    lineStart: z.number().int(),
    lineEnd: z.number().int(),
    confidenceScore: z.number().min(0).max(1),
    extractionMethod: ExtractionMethodEnum,
    businessDomain: z.string().optional(),
    stakeholders: z.array(z.string()).optional(),
    impactLevel: z.enum(['HIGH', 'MEDIUM', 'LOW']).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const PaginationInfoSchema = z.object({
    currentPage: z.number().int().positive(),
    totalPages: z.number().int().nonnegative(),
    totalRecords: z.number().int().nonnegative(),
    pageSize: z.number().int().positive(),
});
export const BusinessRulesResponseSchema = z.object({
    rules: z.array(BusinessRuleSchema),
    pagination: PaginationInfoSchema,
});
export const ModuleSchema = z.lazy(() => z.object({
    id: z.string().cuid(),
    moduleName: z.string(),
    moduleType: ModuleTypeEnum,
    parentModuleId: z.string().cuid().optional(),
    cohesionScore: z.number().min(0).max(1).optional(),
    fileCount: z.number().int(),
    functionCount: z.number().int(),
    businessDomains: z.array(z.string()),
    files: z.array(z.string().cuid()),
    subModules: z.array(ModuleSchema).optional(),
}));
export const ModulesResponseSchema = z.object({
    modules: z.array(ModuleSchema),
    totalModules: z.number().int(),
});
export const KGNodeSchema = z.object({
    id: z.string().cuid(),
    type: NodeTypeEnum,
    label: z.string(),
    properties: z.record(z.any()),
});
export const KGEdgeSchema = z.object({
    from: z.string().cuid(),
    to: z.string().cuid(),
    type: EdgeTypeEnum,
    properties: z.record(z.any()).optional(),
});
export const KnowledgeGraphResponseSchema = z.object({
    nodes: z.array(KGNodeSchema),
    edges: z.array(KGEdgeSchema),
    metrics: z.object({
        totalNodes: z.number().int(),
        totalEdges: z.number().int(),
        nodeTypeCounts: z.record(NodeTypeEnum, z.number().int()),
        edgeTypeCounts: z.record(EdgeTypeEnum, z.number().int()),
    }),
});
export const UnusedFunctionSchema = z.object({
    id: z.string().cuid(),
    name: z.string(),
    fileId: z.string().cuid(),
    filePath: z.string(),
    lineStart: z.number().int(),
    lineEnd: z.number().int(),
    reason: z.string(),
});
export const UnreachableBlockSchema = z.object({
    id: z.string().cuid(),
    functionId: z.string().cuid(),
    functionName: z.string(),
    fileId: z.string().cuid(),
    filePath: z.string(),
    lineStart: z.number().int(),
    lineEnd: z.number().int(),
    reason: z.string(),
});
export const DeadCodeResponseSchema = z.object({
    unusedFunctions: z.array(UnusedFunctionSchema),
    unreachableBlocks: z.array(UnreachableBlockSchema),
    metrics: z.object({
        totalUnusedFunctions: z.number().int(),
        totalUnreachableBlocks: z.number().int(),
        potentialSavings: z.object({
            lines: z.number().int(),
            files: z.number().int(),
        }),
    }),
});
export const ImpactAnalysisResponseSchema = z.object({
    affectedFunctions: z.array(z.string()),
    affectedFiles: z.array(z.string()),
    affectedModules: z.array(z.string()),
    impactScore: z.number().min(0).max(100),
    riskLevel: z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']),
    recommendations: z.array(z.string()),
});
export const ErrorResponseSchema = z.object({
    success: z.literal(false),
    error: z.string(),
    details: z.string().optional(),
});
export const validateAnalyzeFileRequest = (data) => AnalyzeFileRequestSchema.parse(data);
export const validateAnalyzeRepositoryRequest = (data) => AnalyzeRepositoryRequestSchema.parse(data);
export const validateQueryBusinessRules = (data) => QueryBusinessRulesSchema.parse(data);
export const validateQueryCallGraph = (data) => QueryCallGraphSchema.parse(data);
export const validateQueryDeadCode = (data) => QueryDeadCodeSchema.parse(data);
export const validateQueryKnowledgeGraph = (data) => QueryKnowledgeGraphSchema.parse(data);
//# sourceMappingURL=code-analysis-schemas.js.map